import React, { useEffect, useState, useRef } from "react";
import {
  getSimpleEventSubEventDetail,
  toggleSimpleSubEventLike,
  getcomments,
  getSubcomments,
  postComment,
  postSubComment,
  likeComment,
  deleteComment,
  deleteSubComment,
} from "./utilities/ApiCalls";
import { getTokenDetails } from "./utilities/CommonFunction";
import Layout from "../Layout/Layout";
import { useParams } from "react-router-dom";
import { setPageTitle } from "../utils/titleUtils";
import styled from "styled-components";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import { FaHeart, FaRegHeart, FaRegComment, FaShare } from "react-icons/fa";
import { SendHorizonalIcon, ThumbsUp, Trash } from "lucide-react";
import moment from "moment";
import Default from "../Images/Defaultprofile.png";

const token = localStorage.getItem("loginDetails");
const { _id } = getTokenDetails(token);

const SimpleEventSubEvent = () => {
  const [subEventData, setSubEventData] = useState(null);
  const [likeLoading, setLikeLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [subComment, setSubComment] = useState("");
  const [userComments, setUserComments] = useState([]);
  const [replyOpen, setReplyOpen] = useState("");
  const [viewAll, setViewAll] = useState("");
  const [commentLike, setCommentLike] = useState(false);
  const [isPosted, setIsPosted] = useState(false);
  const [isSubCommentPosted, setIsSubCommentPosted] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const { id } = useParams();

  useEffect(() => {
    setPageTitle(location.pathname);
  }, [location]);

  useEffect(() => {
    fetchSubEventData(id);
  }, [id]);

  useEffect(() => {
    if (subEventData?._id) {
      fetchComments(subEventData._id);
    }
  }, [subEventData, isPosted, isSubCommentPosted, commentLike]);

  const fetchSubEventData = async (subEventId) => {
    try {
      const response = await getSimpleEventSubEventDetail(subEventId);
      setSubEventData(response.data);
    } catch (error) {
      console.error("Error fetching sub event data:", error);
      setSubEventData(undefined);
    }
  };

  const fetchComments = async (eventId) => {
    try {
      let userCommentsArray = [];
      const commentsResponse = await getcomments(eventId);
      const commentsData = commentsResponse.data;

      await Promise.all(
        commentsData.map(async (item) => {
          const subCommentsResponse = await getSubcomments(item._id);
          const subCommentsData = subCommentsResponse.data;

          userCommentsArray.push({
            userComment: item,
            userSubComments: subCommentsData,
          });
        })
      );
      userCommentsArray.sort((a, b) => {
        return (
          new Date(a.userComment.createdAt) - new Date(b.userComment.createdAt)
        );
      });
      setUserComments(userCommentsArray);
    } catch (error) {
      console.error("Error fetching comments and subcomments:", error);
    }
  };

  const Duration = (createdAt) => {
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const currentDate = Date.now();
    const differenceInMilliseconds =
      currentDate - new Date(createdAt).getTime();
    const days = Math.floor(differenceInMilliseconds / millisecondsPerDay);

    if (days <= 0) {
      return "Today";
    }
    return `${days} days`;
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    try {
      const response = await postComment(id, comment);
      // console.log("Comment posted:", response);
      setComment("");
      setIsPosted((curr) => !curr);
    } catch (error) {
      console.error("Error posting comment:", error);
    }
  };

  const handleSubmitSubComment = async (commentId) => {
    try {
      const response = await postSubComment(commentId, subComment);
      // console.log("Sub-comment posted:", response);
      setSubComment("");
      setIsSubCommentPosted((curr) => !curr);
      setReplyOpen(false);
    } catch (error) {
      console.error("Error posting sub-comment:", error);
    }
  };

  const handleCommentLike = async (commentId) => {
    try {
      const response = await likeComment(commentId);
      // console.log("Comment like response:", response);
      setCommentLike(!commentLike);
    } catch (error) {
      console.error("Error liking comment:", error);
    }
  };

  const handleCommentDelete = async (commentId) => {
    try {
      const response = await deleteComment(commentId);
      // console.log("Delete comment response:", response);
      if (response.status === 200) {
        // console.log("Comment successfully deleted");
        setIsPosted((curr) => !curr);
      } else {
        console.error("Failed to delete comment:", response.message);
      }
    } catch (error) {
      console.error("Error occurred while deleting comment:", error);
    }
  };

  const handleSubCommentDelete = async (commentId) => {
    try {
      const response = await deleteSubComment(commentId);
      // console.log("Delete sub-comment response:", response);
      if (response.status === 200) {
        // console.log("Sub-comment successfully deleted");
        setIsPosted((curr) => !curr);
      } else {
        console.error("Failed to delete sub-comment:", response.message);
      }
    } catch (error) {
      console.error("Error occurred while deleting sub-comment:", error);
    }
  };

  const toggleMenu = (commentId) => {
    setActiveMenu((prev) => (prev === commentId ? null : commentId));
  };

  // Helper to get YouTube embed link
  const getEmbedLink = (videoLink) => {
    if (!videoLink) return "";
    return videoLink
      .replace("youtu.be/", "www.youtube.com/embed/")
      .replace("watch?v=", "embed/");
  };

  // Like button logic
  const hasLiked =
    subEventData &&
    Array.isArray(subEventData.likes) &&
    subEventData.likes.includes(_id);
  const likeCount =
    subEventData && Array.isArray(subEventData.likes)
      ? subEventData.likes.length
      : 0;

  // Calculate total comment count (comments + subcomments)
  const totalCommentCount = userComments.reduce((total, commentData) => {
    return total + 1 + commentData.userSubComments.length;
  }, 0);

  const handleLike = async () => {
    if (!subEventData) return;
    // Optimistic update
    let newLikes;
    if (hasLiked) {
      newLikes = subEventData.likes.filter((uid) => uid !== _id);
    } else {
      newLikes = [...subEventData.likes, _id];
    }
    setSubEventData({ ...subEventData, likes: newLikes });
    setLikeLoading(true);
    let timeoutId = setTimeout(() => setLikeLoading(false), 2000);
    try {
      const response = await toggleSimpleSubEventLike(subEventData._id, _id);
      // If server returns updated like status, sync it
      if (response && response.status === 1) {
        // Optionally, you could re-fetch likes from server here for full sync
        // But for now, we trust the optimistic update
      }
    } catch (error) {
      // Revert optimistic update on error
      setSubEventData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          likes: hasLiked
            ? [...prev.likes, _id]
            : prev.likes.filter((uid) => uid !== _id),
        };
      });
      // Optionally, show error to user
    } finally {
      clearTimeout(timeoutId);
      setLikeLoading(false);
    }
  };

  // Null check and fallback UI
  if (!subEventData || typeof subEventData !== "object" || !subEventData._id) {
    return (
      <Layout>
        <Wrapper>
          <div
            style={{
              color: "var(--color-text-tertiary)",
              textAlign: "center",
              padding: 40,
              background: "transparent",
            }}
          >
            Loading event details...
          </div>
        </Wrapper>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ background: "transparent", minHeight: "100vh" }}>
        <Wrapper>
          <SubEventsContainer>
            <div className="player">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                }}
              >
                <h1
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "32px",
                    marginBottom: "26px",
                    color: "var(--color-text-tertiary)",
                  }}
                >
                  {subEventData ? subEventData.name : "Loading..."}
                </h1>
              </div>
              <div className="">
                {subEventData?.videoLink && (
                  <iframe
                    credentialless="true"
                    width="100%"
                    height="400"
                    src={getEmbedLink(subEventData.videoLink)}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{
                      borderRadius: "12px",
                    }}
                  ></iframe>
                )}
              </div>
              {/* Like button and count below video */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "24px",
                  marginBottom: "10px",
                }}
              >
                <LikeButton
                  onClick={handleLike}
                  disabled={likeLoading}
                  aria-label={hasLiked ? "Unlike" : "Like"}
                  $liked={hasLiked}
                >
                  {hasLiked ? <FaHeart /> : <FaRegHeart />}{" "}
                  <span className="like-count">{likeCount}</span>
                </LikeButton>
                <ActionButton aria-label="Comment">
                  <FaRegComment />
                  <span className="comment-count">{totalCommentCount}</span>
                </ActionButton>
                <ActionButton aria-label="Share">
                  <FaShare />
                </ActionButton>
              </div>
            </div>
          </SubEventsContainer>

          {/* Comment Section */}
          <CommentSection>
            <CommentForm>
              <CommentInput
                ref={inputRef}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment..."
              />
              <SendButton onClick={handleSubmitComment}>
                <SendHorizonalIcon />
              </SendButton>
            </CommentForm>

            {userComments.length > 0 ? (
              <CommentList>
                {userComments.map((data) => {
                  const duration = Duration(data.userComment.createdAt) || 0;

                  return (
                    <CommentCard key={data.userComment._id}>
                      <CommentHeader>
                        <UserInfo>
                          <UserAvatar
                            src={data.userComment.userProfilePicture || Default}
                            alt="profile"
                            crossOrigin="anonymous"
                          />
                          <UserName>{data.userComment.userName}</UserName>
                        </UserInfo>
                        <CommentActions>
                          <CommentLikeButton
                            onClick={() =>
                              handleCommentLike(data.userComment._id)
                            }
                          >
                            {data.userComment.likes.some(
                              (like) => like.userId === _id
                            ) ? (
                              <ThumbsUp
                                style={{
                                  height: "15px",
                                  width: "15px",
                                  cursor: "pointer",
                                  fill: "white",
                                }}
                              />
                            ) : (
                              <ThumbsUp
                                style={{
                                  height: "15px",
                                  width: "15px",
                                  cursor: "pointer",
                                }}
                              />
                            )}
                          </CommentLikeButton>

                          {data.userComment.userId === _id && (
                            <MenuContainer>
                              <MenuButton
                                onClick={() => toggleMenu(data.userComment._id)}
                              >
                                <i className="fa fa-ellipsis-h" />
                              </MenuButton>

                              {activeMenu === data.userComment._id && (
                                <MenuDropdown>
                                  <MenuItem
                                    onClick={() => {
                                      handleCommentDelete(data.userComment._id);
                                      setActiveMenu(null);
                                    }}
                                  >
                                    <Trash
                                      style={{
                                        height: "15px",
                                        width: "15px",
                                        color: "red",
                                      }}
                                    />
                                    <span>Delete</span>
                                  </MenuItem>
                                </MenuDropdown>
                              )}
                            </MenuContainer>
                          )}
                        </CommentActions>
                      </CommentHeader>

                      <CommentText>{data.userComment.description}</CommentText>

                      <CommentMeta>
                        <MetaItem>{duration}</MetaItem>
                        <MetaItem>
                          {data.userComment.likes.length} Likes
                        </MetaItem>
                        <ReplyButton
                          onClick={() => {
                            setReplyOpen(data.userComment._id);
                          }}
                        >
                          Reply
                        </ReplyButton>
                      </CommentMeta>

                      {data.userSubComments.length > 0 && (
                        <ViewRepliesButton
                          onClick={() => {
                            if (replyOpen !== data.userComment._id) {
                              setViewAll(data.userComment._id);
                              setReplyOpen(data.userComment._id);
                            } else {
                              setReplyOpen("");
                            }
                          }}
                        >
                          {replyOpen !== data.userComment._id
                            ? "----View Replies"
                            : "---Hide Replies"}
                        </ViewRepliesButton>
                      )}

                      {replyOpen === data.userComment._id && (
                        <ReplyForm>
                          <ReplyInput
                            value={subComment}
                            onChange={(e) => setSubComment(e.target.value)}
                            placeholder="Add a reply..."
                          />
                          <ReplySendButton
                            onClick={() =>
                              handleSubmitSubComment(data.userComment._id)
                            }
                          >
                            Reply
                          </ReplySendButton>
                        </ReplyForm>
                      )}

                      {replyOpen &&
                        viewAll === data.userComment._id &&
                        data.userSubComments.length > 0 &&
                        data.userSubComments.map((subData) => {
                          const durationSub = Duration(subData.createdAt);

                          return (
                            <SubCommentCard key={subData._id}>
                              <SubCommentHeader>
                                <UserInfo>
                                  <SubUserAvatar
                                    src={
                                      subData.userProfilePicture &&
                                      subData.userProfilePicture !== ""
                                        ? subData.userProfilePicture
                                        : Default
                                    }
                                    alt=""
                                    crossOrigin="anonymous"
                                  />
                                  <UserName>{subData.userName}</UserName>
                                </UserInfo>
                                <CommentActions>
                                  <CommentLikeButton
                                    onClick={() =>
                                      handleCommentLike(subData._id)
                                    }
                                  >
                                    {subData.likes.some(
                                      (like) => like.userId === _id
                                    ) ? (
                                      <ThumbsUp
                                        style={{
                                          height: "15px",
                                          width: "15px",
                                          cursor: "pointer",
                                          fill: "white",
                                        }}
                                      />
                                    ) : (
                                      <ThumbsUp
                                        style={{
                                          height: "15px",
                                          width: "15px",
                                          cursor: "pointer",
                                        }}
                                      />
                                    )}
                                  </CommentLikeButton>

                                  {subData.userId === _id && (
                                    <MenuContainer>
                                      <MenuButton
                                        onClick={() => {
                                          toggleMenu(subData._id);
                                        }}
                                      >
                                        <i className="fa fa-ellipsis-h" />
                                      </MenuButton>

                                      {activeMenu === subData._id && (
                                        <MenuDropdown>
                                          <MenuItem
                                            onClick={() => {
                                              handleSubCommentDelete(
                                                subData._id
                                              );
                                              setActiveMenu(null);
                                            }}
                                          >
                                            <Trash
                                              style={{
                                                height: "15px",
                                                width: "15px",
                                                color: "red",
                                              }}
                                            />
                                            <span>Delete</span>
                                          </MenuItem>
                                        </MenuDropdown>
                                      )}
                                    </MenuContainer>
                                  )}
                                </CommentActions>
                              </SubCommentHeader>

                              <SubCommentText>
                                {subData.description}
                              </SubCommentText>

                              <SubCommentMeta>
                                <MetaItem>{durationSub}</MetaItem>
                                <MetaItem>
                                  {subData.likes.length} Likes
                                </MetaItem>
                              </SubCommentMeta>
                            </SubCommentCard>
                          );
                        })}
                    </CommentCard>
                  );
                })}
              </CommentList>
            ) : (
              <EmptyComments>
                <EmptyText>No comments</EmptyText>
              </EmptyComments>
            )}
          </CommentSection>
        </Wrapper>
      </div>
    </Layout>
  );
};

export default SimpleEventSubEvent;

// Styled-components moved to bottom
const SubEventsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 15px;
  padding-bottom: 15px;
  background-color: transparent;
  min-height: fit-content;

  hr {
    border: 0.2px solid var(--color-border);
  }
`;

const Wrapper = styled.div`
  max-width: 1350px;
  margin: 0 auto;
  width: 100%;
  background-color: transparent;
`;

const LikeButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  font-size: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-top: 20px;
  margin-bottom: 20px;
  transition: color 0.2s;
  svg {
    color: ${({ $liked }) =>
      $liked ? "#ff4444" : "var(--color-text-tertiary)"};
    transition: color 0.2s;
  }
  .like-count {
    color: ${({ $liked }) =>
      $liked ? "#ff4444" : "var(--color-text-tertiary)"};
    text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    font-family: "Segoe UI";
    font-size: 24px;
  }
  &:disabled {
    opacity: 0.5;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  font-size: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-top: 20px;
  margin-bottom: 20px;
  transition: color 0.2s;
  svg {
    color: var(--color-text-tertiary);
    transition: color 0.2s;
  }
  .comment-count {
    color: var(--color-text-tertiary);
    text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    font-family: "Segoe UI";
    font-size: 24px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Comment Section Styled Components
const CommentSection = styled.div`
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding-bottom: 20px;
  margin-top: 20px;

  @media (max-width: 1300px) {
    padding: 0 10vw;
  }
`;

const CommentForm = styled.form`
  position: relative;
  margin-bottom: 20px;
`;

const CommentInput = styled.input`
  width: 100%;
  border-radius: 20px;
  outline: none;
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  padding: 12px 50px 12px 15px;
  color: var(--color-text-tertiary);
  font-size: 14px;

  &::placeholder {
    color: var(--color-text-tertiary);
    opacity: 0.7;
  }

  &:focus {
    border-color: #52b2cf;
  }
`;

const SendButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  outline: none;
  background: none;
  color: var(--color-text-tertiary);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--color-surface);
  }
`;

const CommentList = styled.div`
  width: 100%;
`;

const CommentCard = styled.div`
  margin-bottom: 20px;
  padding: 0;
  background: none;
  border: none;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UserAvatar = styled.img`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  object-fit: cover;
`;

const SubUserAvatar = styled(UserAvatar)`
  width: 30px;
  height: 30px;
`;

const UserName = styled.span`
  font-size: 15px;
  font-weight: bold;
  color: var(--color-text-tertiary);
`;

const CommentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CommentLikeButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--color-surface);
  }
`;

const MenuContainer = styled.div`
  position: relative;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--color-surface);
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 100;
  min-width: 120px;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  width: 100%;
  text-align: left;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--color-surface);
  }

  span {
    font-size: 14px;
  }
`;

const CommentText = styled.div`
  color: var(--color-text-tertiary);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 5px;
`;

const CommentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 12px;
  color: var(--color-text-tertiary);
  opacity: 0.8;
`;

const MetaItem = styled.span``;

const ReplyButton = styled.button`
  background: none;
  border: none;
  color: #52b2cf;
  cursor: pointer;
  font-size: 12px;
  transition: color 0.2s;

  &:hover {
    color: #7dd3e8;
  }
`;

const ViewRepliesButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  opacity: 0.7;
  cursor: pointer;
  font-size: 12px;
  margin-top: 10px;
  transition: color 0.2s;

  &:hover {
    color: var(--color-text-tertiary);
    opacity: 1;
  }
`;

const ReplyForm = styled.form`
  position: relative;
  margin-top: 15px;
  margin-left: 45px;
`;

const ReplyInput = styled.input`
  width: 100%;
  border-radius: 20px;
  outline: none;
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  padding: 10px 50px 10px 15px;
  color: var(--color-text-tertiary);
  font-size: 13px;

  &::placeholder {
    color: var(--color-text-tertiary);
    opacity: 0.7;
  }

  &:focus {
    border-color: #52b2cf;
  }
`;

const ReplySendButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  outline: none;
  background: none;
  color: var(--color-text-tertiary);
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  font-size: 12px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--color-surface);
  }
`;

const SubCommentCard = styled.div`
  margin-top: 15px;
  margin-left: 45px;
  padding: 0;
  background: none;
  border: none;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SubCommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const SubCommentText = styled.div`
  color: var(--color-text-tertiary);
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 8px;
`;

const SubCommentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 11px;
  color: var(--color-text-tertiary);
  opacity: 0.8;
`;

const EmptyComments = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
`;

const EmptyText = styled.div`
  color: var(--color-text-tertiary);
  opacity: 0.7;
  font-size: 16px;
`;
